import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  DragIndicator,
  FitScreen,
  Fullscreen,
  FullscreenExit,
  ZoomIn,
  ZoomOut,
  ArrowBackIos,
  ArrowForwardIos,
} from "@mui/icons-material";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { SlideNavigationContext } from "./SlideNavigationContext";

const BASE_WIDTH = 1280;
const BASE_HEIGHT = 720;
const THUMB_WIDTH = 104;
const THUMB_SCALE = THUMB_WIDTH / BASE_WIDTH;
const MOBILE_WINDOW = 2;
const BATCH_SIZE = 10;
const BATCH_DELAY = 3000;

// Smooth easeInOut scroll — centers the thumbnail in the sidebar
function smoothScrollCenter(container, target, duration = 360) {
  const startTop = container.scrollTop;
  const thumbTop = target.offsetTop;
  const thumbH = target.offsetHeight;
  const containerH = container.clientHeight;
  const endTop = Math.max(0, thumbTop - containerH / 2 + thumbH / 2);
  const distance = endTop - startTop;
  if (Math.abs(distance) < 2) return;
  const t0 = performance.now();
  const ease = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  const step = (now) => {
    const progress = Math.min((now - t0) / duration, 1);
    container.scrollTop = startTop + distance * ease(progress);
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// ─── Thumbnail ────────────────────────────────────────────────────────────────
function SlideThumbnail({ getSlideEl, index, currentSlide, navigateToSlide, thumbnailContainerRef, disableThumbnail }) {
  const containerRef = useRef(null);
  const thumbnailRef = useRef(null);
  const isActive = currentSlide === index;

  useEffect(() => {
    if (disableThumbnail) return;

    let cloneEl = null;
    let observer = null;
    let debounceTimer = null;
    const containerNode = containerRef.current;

    const updateClone = () => {
      const slideEl = getSlideEl(index);
      if (!slideEl || !containerNode) return;
      if (cloneEl && containerNode.contains(cloneEl)) containerNode.removeChild(cloneEl);
      cloneEl = slideEl.cloneNode(true);
      Object.assign(cloneEl.style, {
        position: "absolute",
        top: "0",
        left: "0",
        width: `${BASE_WIDTH}px`,
        height: `${BASE_HEIGHT}px`,
        transform: `scale(${THUMB_SCALE})`,
        transformOrigin: "top left",
        pointerEvents: "none",
        userSelect: "none",
      });
      containerNode.appendChild(cloneEl);
    };

    const scheduleUpdate = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updateClone, 400);
    };

    const initTimer = setTimeout(() => {
      updateClone();
      const slideEl = getSlideEl(index);
      if (slideEl) {
        observer = new MutationObserver(scheduleUpdate);
        observer.observe(slideEl, { childList: true, subtree: true, characterData: true });
      }
    }, 300);

    return () => {
      clearTimeout(initTimer);
      clearTimeout(debounceTimer);
      observer?.disconnect();
      if (cloneEl && containerNode?.contains(cloneEl)) containerNode.removeChild(cloneEl);
    };
  }, [index, getSlideEl, disableThumbnail]);

  useEffect(() => {
    if (isActive && thumbnailRef.current && thumbnailContainerRef?.current) {
      smoothScrollCenter(thumbnailContainerRef.current, thumbnailRef.current);
    }
  }, [isActive, thumbnailContainerRef]);

  return (
    <Box
      ref={thumbnailRef}
      onClick={() => navigateToSlide(index)}
      sx={{
        width: "100%",
        height: 59,
        mb: 1,
        borderRadius: 1,
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        bgcolor: "#fff",
        border: isActive ? "2px solid #1976d2" : "1.5px solid #ccc",
        boxShadow: isActive
          ? "0 0 0 3px rgba(25,118,210,0.18), 0 3px 10px rgba(25,118,210,0.12)"
          : "0 1px 3px rgba(0,0,0,0.08)",
        transform: isActive ? "scale(1.04)" : "scale(1)",
        transition: "border 0.22s cubic-bezier(0.4,0,0.2,1), box-shadow 0.22s cubic-bezier(0.4,0,0.2,1), transform 0.22s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": {
          transform: isActive ? "scale(1.04)" : "scale(1.02) translateY(-2px)",
          boxShadow: isActive
            ? "0 0 0 3px rgba(25,118,210,0.18), 0 3px 10px rgba(25,118,210,0.12)"
            : "0 4px 14px rgba(0,0,0,0.14)",
          borderColor: isActive ? "#1976d2" : "#bbb",
        },
      }}
    >
      <Box
        ref={containerRef}
        sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", overflow: "hidden" }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 1,
          right: 1,
          minWidth: 12,
          height: 16,
          bgcolor: isActive ? "#1976d2" : "rgba(0,0,0,0.50)",
          borderRadius: "3px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 0.5,
          zIndex: 1,
          transition: "background-color 0.22s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <Typography
          sx={{
            fontSize: 8,
            fontWeight: 700,
            color: "#fff",
            userSelect: "none",
            lineHeight: 1,
            letterSpacing: "0.02em",
          }}
        >
          {index + 1}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── LazySlide Component ──────────────────────────────────────────────────────
const LazySlide = ({ children, index, onMount, isLazyLoading, currentSlide, loadedCount }) => {
  const ref = useRef(null);
  const [cachedHeight, setCachedHeight] = useState(BASE_HEIGHT);

  // Three states:
  // 1. Not yet in loaded batch  → locked placeholder
  // 2. In batch but far away    → virtualized (unmounted, cached height)
  // 3. In batch and nearby      → fully mounted
  const isInBatch = !isLazyLoading || index < loadedCount;
  const isNearby  = isInBatch && (!isLazyLoading || Math.abs(index - currentSlide) <= MOBILE_WINDOW);
  const [isMounted, setIsMounted] = useState(isNearby);

  useEffect(() => {
    if (ref.current) onMount(index, ref.current);
  }, [index, onMount]);

  useEffect(() => {
    if (!isLazyLoading) return;

    if (isNearby) {
      const t = setTimeout(() => setIsMounted(true), 0);
      return () => clearTimeout(t);
    } else {
      if (ref.current && ref.current.offsetHeight > 0) {
        setCachedHeight(ref.current.offsetHeight);
      }
      const t = setTimeout(() => setIsMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [isNearby, isLazyLoading]);

  return (
    <Box
      ref={ref}
      sx={{
        width: BASE_WIDTH,
        minHeight: BASE_HEIGHT,
        bgcolor: "#fff",
        borderRadius: 3,
        boxShadow: 4,
        overflow: "hidden",
      }}
    >
      {isMounted ? children : (
        <Box
          sx={{
            width: "100%",
            height: isInBatch ? cachedHeight : BASE_HEIGHT,
            bgcolor: isInBatch ? "#f5f5f5" : "#ececec",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "#bbb", userSelect: "none" }}>
            {isInBatch ? `Slide ${index + 1}` : `Slide ${index + 1} — loading...`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

function MobileSlideViewer({ slideArray, slideCount }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [zoom, setZoom] = useState(1);

  const slideRef     = useRef(null);
  const containerRef = useRef(null);
  const navRef       = useRef(null);
  const wrapperRef   = useRef(null);
  const pinchRef     = useRef(null);

  const [navH, setNavH]     = useState(72);
  const [availH, setAvailH] = useState(window.innerHeight);

  const MIN_ZOOM = 0.3;
  const MAX_ZOOM = 3;

  // ── Lock body scroll + disable browser viewport pinch-zoom ─────────────
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const meta = document.querySelector('meta[name="viewport"]');
    const prevContent = meta?.getAttribute("content") ?? "";
    meta?.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no");

    return () => {
      document.body.style.overflow = prevOverflow;
      if (meta && prevContent) meta.setAttribute("content", prevContent);
    };
  }, []);

  // ── Block browser native pinch-to-zoom on entire page (filter safe rahega) ─
  useEffect(() => {
    const preventPinchZoom = (e) => {
      if (e.touches.length >= 2) e.preventDefault();
    };
    window.addEventListener("touchmove", preventPinchZoom, { passive: false });
    return () => window.removeEventListener("touchmove", preventPinchZoom);
  }, []);

  // ── Available height ────────────────────────────────────────────────────
  useEffect(() => {
    const measure = () => {
      if (!wrapperRef.current) return;
      const top = wrapperRef.current.getBoundingClientRect().top;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      setAvailH(vh - top);
    };
    measure();
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, []);

  // ── Nav height ──────────────────────────────────────────────────────────
  useEffect(() => {
    const measureNav = () => {
      if (navRef.current) setNavH(navRef.current.offsetHeight);
    };
    measureNav();
    window.addEventListener("resize", measureNav);
    return () => window.removeEventListener("resize", measureNav);
  }, []);

  // ── Navigation ──────────────────────────────────────────────────────────
  const goNext = useCallback(() => setCurrentSlide((p) => Math.min(p + 1, slideCount - 1)), [slideCount]);
  const goPrev = useCallback(() => setCurrentSlide((p) => Math.max(p - 1, 0)), []);
  const navigateToSlide = useCallback(
    (index) => setCurrentSlide(Math.min(Math.max(index, 0), slideCount - 1)),
    [slideCount]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
      if (e.key === "ArrowLeft"  || e.key === "ArrowUp")   goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  // ── Fit to screen (default on mount/resize) ─────────────────────────────
  const fitToScreen = useCallback(() => {
    if (!containerRef.current) return;
    const scaleX = containerRef.current.clientWidth  / BASE_WIDTH;
    const scaleY = containerRef.current.clientHeight / BASE_HEIGHT;
    setZoom(Math.min(scaleX, scaleY) * 0.95);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(fitToScreen);
    ro.observe(containerRef.current);
    fitToScreen();
    return () => ro.disconnect();
  }, [fitToScreen]);

  // ── Clamp helper ────────────────────────────────────────────────────────
  const clampZoom = (v) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v));

  const applyZoom = useCallback((newZoom) => {
    setZoom(clampZoom(newZoom));
  }, []);

  // ── Pinch-to-zoom (native non-passive listeners — React synthetic touchmove is passive, preventDefault ignored) ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onStart = (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchRef.current = { startDist: Math.hypot(dx, dy), startZoom: zoom };
      }
    };

    const onMove = (e) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        applyZoom(pinchRef.current.startZoom * (dist / pinchRef.current.startDist));
      }
    };

    const onEnd = () => { pinchRef.current = null; };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove",  onMove,  { passive: false });
    el.addEventListener("touchend",   onEnd,   { passive: true });

    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove",  onMove);
      el.removeEventListener("touchend",   onEnd);
    };
  }, [zoom, applyZoom]);

  // ── Scaled dimensions — yahi scroll fix ka core hai ────────────────────
  // transform: scale() sirf visually scale karta hai, DOM layout nahi badlata
  // isliye hum actual width/height set kar rahe hain zoom se multiply karke
  const scaledW = Math.round(BASE_WIDTH  * zoom);
  const scaledH = Math.round(BASE_HEIGHT * zoom);

  return (
    <SlideNavigationContext.Provider value={navigateToSlide}>
    <Box
      ref={wrapperRef}
      sx={{ position: "relative", height: `${availH}px`, bgcolor: "#fff", overflow: "hidden" }}
    >

      {/* ── Scrollable content area ── */}
      <Box
        ref={containerRef}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: `${navH}px`,
          overflow: "auto",                  // always auto — HR + VR scroll dono
          WebkitOverflowScrolling: "touch",
          bgcolor: "#f4f4f4",
          // Center karo jab content chota ho viewport se
          display: "flex",
          alignItems: scaledH < (availH - navH) ? "center" : "flex-start",
          justifyContent: scaledW < window.innerWidth ? "center" : "flex-start",
        }}
      >
        {/*
          KEY FIX:
          Ek inner wrapper jo exact scaled size leta hai.
          Iske andar slide ko transform: scale() se render karo
          lekin transformOrigin "top left" rakho — toh content
          top-left se start hoga aur scroll bilkul sahi kaam karegi.
        */}
        <Box
          sx={{
            width:     `${scaledW}px`,   // actual DOM space = scaled size
            height:    `${scaledH}px`,   // browser is se scrollbar calculate karega
            flexShrink: 0,
            position:  "relative",
            
          }}
        >
          <Box
            ref={slideRef}
            sx={{
              width:           `${BASE_WIDTH}px`,
              // height:          `${BASE_HEIGHT}px`,
              transform:       `scale(${zoom})`,
              transformOrigin: "top left",        // top left — DOM space ke saath sync
              position:        "absolute",
              top:             0,
              left:            0,
              bgcolor:         "#fff",
              borderRadius:    2,
              boxShadow:       3,
              overflow:        "auto",
            }}
          >
            {slideArray[currentSlide]}
          </Box>
        </Box>
      </Box>

      {/* ── Bottom navigation ── */}
      <Box
        ref={navRef}
        data-no-export
        sx={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          bgcolor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(10px)",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        {/* Prev */}
        <IconButton
          onClick={goPrev}
          disabled={currentSlide === 0}
          sx={{
            bgcolor: currentSlide === 0 ? "rgba(0,0,0,0.05)" : "#1976d2",
            color:   currentSlide === 0 ? "#999" : "#fff",
            "&:hover": { bgcolor: currentSlide === 0 ? "rgba(0,0,0,0.05)" : "#1565c0" },
            width: 44, height: 44,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <ArrowBackIos sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Slide counter */}
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
          <span style={{ color: "#1976d2", fontWeight: 700, fontSize: 15 }}>{currentSlide + 1}</span>
          {" / "}
          {slideCount}
        </Typography>

        {/* Next */}
        <IconButton
          onClick={goNext}
          disabled={currentSlide === slideCount - 1}
          sx={{
            bgcolor: currentSlide === slideCount - 1 ? "rgba(0,0,0,0.05)" : "#1976d2",
            color:   currentSlide === slideCount - 1 ? "#999" : "#fff",
            "&:hover": { bgcolor: currentSlide === slideCount - 1 ? "rgba(0,0,0,0.05)" : "#1565c0" },
            width: 44, height: 44,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <ArrowForwardIos sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
    </SlideNavigationContext.Provider>
  );
}

// ─── Main SlideViewer ─────────────────────────────────────────────────────────
export default function SlideViewer({ children }) {
  const slideArray = Array.isArray(children) ? children : (children ? [children] : []);
  const slideCount = slideArray.length;
  const location = useLocation();
  const path = location.pathname === "/board-pack-mobile";
  const effectiveLazyLoading = path ? true : false;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) || path;

  const [zoom, setZoom] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [presentationMode, setPresentationMode] = useState(false);

  const [loadedCount, setLoadedCount] = useState(() =>
    (!effectiveLazyLoading || slideCount <= 5) ? slideCount : Math.min(BATCH_SIZE, slideCount)
  );

  const containerRef = useRef(null);
  const thumbnailContainerRef = useRef(null);
  const slideRefs = useRef([]);
  const slideWrapperRefs      = useRef([]);
  const isProgrammaticScrollRef = useRef(false);
  const scrollIntentRef = useRef(false);
  const isZoomingRef = useRef(false);
  const lastMouseRef = useRef(null);       // last cursor pos inside container — for zoom-towards-cursor
  const zoomAnchorRef = useRef(null); // { index, fracX, fracY, cx, cy } — slide-relative anchor point for zoom-towards-cursor

  // getSlideEl: thumbnails use this to clone real slide DOM
  const getSlideEl = useCallback((index) => slideRefs.current[index] ?? null, []);

  // registerSlide: called by LazySlide — stores internal ref for thumbnail cloning
  const registerSlide = useCallback((index, el) => {
    slideRefs.current[index] = el;
  }, []);

  // navSeq bumps on every navigateToSlide call — the scroll effect depends on it
  // instead of relying solely on currentSlide changing, so clicking a thumbnail
  // that is already "active" (e.g. after zoom drift) still re-scrolls to it.
  const [navSeq, setNavSeq] = useState(0);
  const navigateToSlide = useCallback((index) => {
    scrollIntentRef.current = true;
    setCurrentSlide(index);
    setNavSeq((s) => s + 1);
  }, []);

  // ── Draggable controls panel ───────────────────────────────────────────────
  const panelRef = useRef(null);
  const dragState = useRef(null);
  const [panelPos, setPanelPos] = useState(null);

  const startDrag = useCallback((clientX, clientY) => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = { startMouseX: clientX, startMouseY: clientY, startLeft: rect.left, startTop: rect.top };
  }, []);

  const onDragMove = useCallback((clientX, clientY) => {
    if (!dragState.current || !panelRef.current) return;
    const { startMouseX, startMouseY, startLeft, startTop } = dragState.current;
    const panelW = panelRef.current.offsetWidth;
    const panelH = panelRef.current.offsetHeight;
    setPanelPos({
      left: Math.max(0, Math.min(window.innerWidth - panelW, startLeft + clientX - startMouseX)),
      top: Math.max(0, Math.min(window.innerHeight - panelH, startTop + clientY - startMouseY)),
    });
  }, []);

  const endDrag = useCallback(() => { dragState.current = null; }, []);

  useEffect(() => {
    if (isMobile) return;
    const onMove = (e) => onDragMove(e.clientX, e.clientY);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", endDrag);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", endDrag);
    };
  }, [onDragMove, endDrag, isMobile]);

  // ── Fit to screen ─────────────────────────────────────────────────────────
  const fitToScreen = useCallback(() => {
    if (!containerRef.current) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    zoomAnchorRef.current = null; // fit-to-screen always centers slide, not towards cursor
    isZoomingRef.current = true;
    isProgrammaticScrollRef.current = true;
    setZoom(Math.min(width / BASE_WIDTH, height / BASE_HEIGHT) * 0.9);
  }, []);

  useEffect(() => {
    fitToScreen();
    window.addEventListener("resize", fitToScreen);
    return () => window.removeEventListener("resize", fitToScreen);
  }, [fitToScreen]);

  // ── Batch loading ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!effectiveLazyLoading || loadedCount >= slideCount) return;
    if (currentSlide < loadedCount - 2) return;
    const t = setTimeout(() => setLoadedCount((p) => Math.min(p + BATCH_SIZE, slideCount)), BATCH_DELAY);
    return () => clearTimeout(t);
  }, [currentSlide, loadedCount, slideCount, effectiveLazyLoading]);

  // ── Visible slide detection — uses slideWrapperRefs (no transform on them) ─
  const detectVisibleSlide = useCallback(() => {
    if (isProgrammaticScrollRef.current || isZoomingRef.current) return;
    let maxArea = 0;
    let best = currentSlide;
    slideWrapperRefs.current.forEach((el, i) => {
      if (!el || !containerRef.current) return;
      const r = el.getBoundingClientRect();
      const c = containerRef.current.getBoundingClientRect();
      const visible = Math.max(0, Math.min(r.bottom, c.bottom) - Math.max(r.top, c.top));
      const pct = visible / r.height;
      if (pct > maxArea) { maxArea = pct; best = i; }
    });
    if (maxArea > 0.2 && best !== currentSlide) setCurrentSlide(best);
  }, [currentSlide]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let t;
    const onScroll = () => {
      if (isProgrammaticScrollRef.current || isZoomingRef.current) return;
      clearTimeout(t);
      t = setTimeout(detectVisibleSlide, effectiveLazyLoading ? 200 : 100);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    setTimeout(detectVisibleSlide, 500);
    return () => { el.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, [detectVisibleSlide, effectiveLazyLoading]);

  // ── Keyboard navigation ────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") navigateToSlide(Math.min(currentSlide + 1, slideCount - 1));
      if (e.key === "ArrowLeft"  || e.key === "ArrowUp")   navigateToSlide(Math.max(currentSlide - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slideCount, currentSlide, navigateToSlide]);

  // ── Manual scroll helper — works correctly because slideWrapperRefs have real DOM dimensions ──
  const scrollToSlide = useCallback((index, behavior = "smooth") => {
    const el = slideWrapperRefs.current[index];
    const container = containerRef.current;
    if (!el || !container) return;
    const elRect        = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const target = container.scrollTop + elRect.top - containerRect.top
                   - (containerRect.height - elRect.height) / 2;
    container.scrollTo({ top: Math.max(0, target), behavior });
  }, []);

  // ── Scroll to slide when navigated ────────────────────────────────────────
  // Depends on navSeq (not just currentSlide) so clicking a thumbnail that is
  // already "active" — e.g. after zoom left the view drifted off it — still re-scrolls.
  useEffect(() => {
    if (!scrollIntentRef.current) return;
    scrollIntentRef.current = false;
    isProgrammaticScrollRef.current = true;
    scrollToSlide(currentSlide, "smooth");
    setTimeout(() => { isProgrammaticScrollRef.current = false; }, 800);
  }, [currentSlide, navSeq, scrollToSlide]);

  // Find which slide sits under a viewport point, and where within it (0..1 fractions).
  // Used instead of scaling raw scrollTop by a zoom ratio, because gaps/padding between
  // slides do NOT scale with each slide's own `zoom` — a uniform-ratio formula drifts
  // further off the further down the deck you are, landing on the wrong slide.
  const findAnchorAtPoint = useCallback((clientX, clientY) => {
    const wrappers = slideWrapperRefs.current;
    for (let i = 0; i < wrappers.length; i++) {
      const el = wrappers[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom) {
        return {
          index: i,
          fracX: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
          fracY: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
        };
      }
    }
    return null;
  }, []);

  // ── Zoom ──────────────────────────────────────────────────────────────────
  const handleZoomChange = useCallback((newZoom, clientX, clientY) => {
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      // use provided coords (Ctrl+Scroll) or last known in-container mouse pos (buttons)
      const cx = clientX ?? lastMouseRef.current?.x ?? (rect.left + rect.width / 2);
      const cy = clientY ?? lastMouseRef.current?.y ?? (rect.top + rect.height / 2);
      const anchor = findAnchorAtPoint(cx, cy);
      zoomAnchorRef.current = anchor ? { ...anchor, cx, cy } : null;
    }
    isZoomingRef.current = true;
    isProgrammaticScrollRef.current = true;
    setZoom(newZoom);
  }, [findAnchorAtPoint]);

  // After zoom: re-align the anchored slide point back under the cursor (or re-center as fallback),
  // then resync currentSlide/thumbnail highlight with whatever actually ended up in view.
  useEffect(() => {
    if (!isZoomingRef.current) return;
    const container = containerRef.current;
    const anchor = zoomAnchorRef.current;
    if (container && anchor) {
      const el = slideWrapperRefs.current[anchor.index];
      if (el) {
        const r = el.getBoundingClientRect();
        const targetX = r.left + anchor.fracX * r.width;
        const targetY = r.top + anchor.fracY * r.height;
        container.scrollLeft += targetX - anchor.cx;
        container.scrollTop  += targetY - anchor.cy;
      }
      zoomAnchorRef.current = null;
    } else {
      scrollToSlide(currentSlide, "instant");
    }
    const t = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      isZoomingRef.current = false;
      detectVisibleSlide();
    }, 500);
    return () => clearTimeout(t);
  }, [zoom, currentSlide, scrollToSlide, detectVisibleSlide]);

  // Track cursor inside container — used for zoom-towards-cursor on button clicks
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e) => { lastMouseRef.current = { x: e.clientX, y: e.clientY }; };
    el.addEventListener("mousemove", onMove, { passive: true });
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  // Ctrl+Scroll to zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      handleZoomChange(Math.min(3, Math.max(0.3, zoom + delta)), e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoom, handleZoomChange]);

  // Mobile route — all hooks already called above, safe to return early now
  if (path) {
    return <MobileSlideViewer slideArray={slideArray} slideCount={slideCount} />;
  }

  const sidebarVisible = !presentationMode && !isMobile;


  return (
    <SlideNavigationContext.Provider value={navigateToSlide}>
    <Box sx={{ position: "relative", height: "100%", overflow: "hidden" }}>

      {/* ── THUMBNAILS SIDEBAR ─────────────────────────────────────────────────
           position:absolute + top:0 + bottom:0  →  definite height guaranteed
           →  overflowY:auto engages correctly (same pattern as MobileSlideViewer)
      ── */}
      {sidebarVisible && (
        <Box
          ref={thumbnailContainerRef}
          data-no-export
          sx={{
            position: "absolute",
            top: 0, left: 0, bottom: 0,
            width: 120,
            overflowY: "auto",
            scrollBehavior: "smooth",
            bgcolor: "#eaeaea",
            p: 1,
            zIndex: 1,
          }}
        >
          {Array.from({ length: slideCount }, (_, i) => (
            <SlideThumbnail
              key={i}
              index={i}
              getSlideEl={getSlideEl}
              currentSlide={currentSlide}
              navigateToSlide={navigateToSlide}
              thumbnailContainerRef={thumbnailContainerRef}
              disableThumbnail={path}
            />
          ))}
        </Box>
      )}

      {/* ── MAIN SCROLL AREA ───────────────────────────────────────────────── */}
      <Box
        ref={containerRef}
        sx={{
          position: "absolute",
          top: 0,
          left: sidebarVisible ? 120 : 0,
          right: 0,
          bottom: 0,
          overflowX: "auto",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          bgcolor: "#f4f6f8",
        }}
      >
        {/* ── DRAGGABLE CONTROLS ── */}
        {!isMobile && (
          <Box
            ref={panelRef}
            sx={{
              position: "fixed",
              ...(panelPos ? { top: panelPos.top, left: panelPos.left } : { bottom: 80, right: 8 }),
              zIndex: 999,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              bgcolor: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(4px)",
              borderRadius: 2,
              p: 1,
              boxShadow: 3,
            }}
          >
            <Box
              onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX, e.clientY); }}
              onTouchStart={(e) => { const t = e.touches[0]; startDrag(t.clientX, t.clientY); }}
              onTouchMove={(e)  => { const t = e.touches[0]; onDragMove(t.clientX, t.clientY); }}
              onTouchEnd={endDrag}
              sx={{ cursor: "grab", display: "flex", justifyContent: "center", color: "#bbb", touchAction: "none", "&:active": { cursor: "grabbing" } }}
            >
              <DragIndicator fontSize="small" />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.5 }}>
              <Tooltip title="Go to slide" placement="left">
                <input
                  type="number"
                  min={1}
                  max={slideCount}
                  defaultValue={currentSlide + 1}
                  key={currentSlide}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const v = parseInt(e.target.value);
                      if (!isNaN(v) && v >= 1 && v <= slideCount) navigateToSlide(v - 1);
                    }
                  }}
                  style={{ width: 32, textAlign: "center", fontSize: 11, fontWeight: 700, border: "1.5px solid #1976d2", borderRadius: 6, padding: "3px 2px", outline: "none", color: "#1976d2" }}
                />
              </Tooltip>
              <Typography sx={{ fontSize: 11, color: "#888", userSelect: "none" }}>/ {slideCount}</Typography>
            </Box>

            <Tooltip title="Zoom In"       placement="left"><IconButton onClick={() => handleZoomChange(zoom + 0.1)}><ZoomIn /></IconButton></Tooltip>
            <Tooltip title="Zoom Out"      placement="left"><IconButton onClick={() => handleZoomChange(Math.max(0.3, zoom - 0.1))}><ZoomOut /></IconButton></Tooltip>
            <Tooltip title="Fit to Screen" placement="left"><IconButton onClick={fitToScreen}><FitScreen /></IconButton></Tooltip>
            <Tooltip title={presentationMode ? "Normal View" : "Presentation Mode"} placement="left">
              <IconButton onClick={() => setPresentationMode((p) => !p)}>
                {presentationMode ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* ── SLIDES ─────────────────────────────────────────────────────────
             MobileSlideViewer ka exact pattern:
               outer Box  →  width:scaledW, height:scaledH  (real DOM space)
               inner Box  →  transform:scale(zoom), transformOrigin:top left
             Scroll container scaledH se height calculate karta hai — no hacks needed
        ── */}
        {/* CSS zoom — layout bhi scale hota hai (transform:scale ke khilaf jo sirf visual tha)
            Height auto rehti hai — lamba content bhi sahi dikhta hai, scroll bhi kaam karta hai */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: "100%", pt: 3, pb: 30, gap: "40px" }}>
          {slideArray.map((child, index) => (
            <Box
              key={index}
              ref={(el) => { slideWrapperRefs.current[index] = el; }}
              sx={{
                zoom: zoom,
                width: `${BASE_WIDTH}px`,
                mx: "auto",
                flexShrink: 0,
              }}
            >
              <LazySlide
                index={index}
                onMount={registerSlide}
                isLazyLoading={effectiveLazyLoading}
                currentSlide={currentSlide}
                loadedCount={loadedCount}
              >
                {child}
              </LazySlide>
            </Box>
          ))}
        </Box>

      </Box>
    </Box>
    </SlideNavigationContext.Provider>
  );
}